import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { ArrowRight, CheckCircle, ExclamationTriangle, Rocket } from 'react-bootstrap-icons';
import type { Client, ECRImage } from '@/types';
import type { ActivityRecord } from '@/types/activity';
import { useAuth } from '@/contexts/AuthContext';
import { useClientFlags } from '@/hooks/useClientFlags';
import { clientService } from '@/services/clientService';
import { activityService } from '@/services/activityService';
import { getEcrService } from '@/services/ecrService';
import { listDeploymentsByTimeRange, type DeploymentRecord } from '@/services/deploymentService';
import { LIVE_LOOKBACK_DAYS, liveImageKey, loadLiveImages, type LiveImages } from '@/services/fleetImageService';
import { fetchPublicDemoStats, type PublicDemoStats } from '@/services/publicDemoService';
import { AVAILABLE_TOOLS } from '@/data/tools';
import { ToolCard } from '@/components/tools/ToolCard';
import { nzToday, recordSummary } from '@/components/clientChecks/clientChecks';
import { PageHeader } from '@/components/ui/PageHeader';
import { Empty, Kpi, KpiGrid, Panel, Section } from '@/components/NumaDashboard/ui/kit';
import { describeActivity } from '@/utils/activityText';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import './Home.css';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const WEEK_PAGE_SIZE = 200;
const WEEK_MAX_PAGES = 6;

/** The tools most people come to the portal for. The rest are one click away on the Tools page. */
const COMMON_TOOL_IDS = [
  'update-client-config',
  'setup-nextgen-client',
  'bulk-update-client-config',
  'announcements',
  'get-system-user-secret',
  'usage-report',
];

const isLive = (dep: DeploymentRecord) => ['running', 'retrying'].includes((dep.status || '').toLowerCase());
const isFailed = (dep: DeploymentRecord) => (dep.status || '').toLowerCase() === 'failed';
const isSuccess = (dep: DeploymentRecord) => (dep.status || '').toLowerCase() === 'success';

/** Every deploy record started in the last seven days, newest first. A fleet release is a few hundred rows. */
async function loadWeekOfDeploys(): Promise<DeploymentRecord[]> {
  const from = new Date(Date.now() - WEEK_MS).toISOString();
  const all: DeploymentRecord[] = [];
  let cursor: Record<string, unknown> | undefined;
  for (let page = 0; page < WEEK_MAX_PAGES; page++) {
    const res = await listDeploymentsByTimeRange({
      fromIso: from,
      pageSize: WEEK_PAGE_SIZE,
      exclusiveStartKey: cursor,
    });
    all.push(...res.items);
    cursor = res.lastEvaluatedKey;
    if (!cursor) break;
  }
  return all;
}

function greeting(): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-NZ', { timeZone: 'Pacific/Auckland', hour: 'numeric', hour12: false }).format(
      new Date()
    )
  );
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function firstName(username?: string, email?: string): string {
  const raw = (username || email || '').split('@')[0].split(/[._-]/)[0];
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

export default function Home() {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[] | null>(null);
  const [week, setWeek] = useState<DeploymentRecord[] | null>(null);
  const [live, setLive] = useState<LiveImages | null>(null);
  const [images, setImages] = useState<ECRImage[]>([]);
  const [activity, setActivity] = useState<ActivityRecord[] | null>(null);
  const [demo, setDemo] = useState<PublicDemoStats[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Each part loads on its own, so a slow read never holds the page back.
  useEffect(() => {
    const fail = (what: string) => (e: unknown) => {
      console.error(`Home: ${what} failed to load`, e);
      setError((prev) => prev || `Some of this page did not load (${what}). Refresh to try again.`);
    };

    clientService
      .getAllClients()
      .then((all) => {
        setClients(all);
        if (all.some((c) => c.config.publicDemo)) {
          fetchPublicDemoStats(all)
            .then(setDemo)
            .catch((e) => console.error('Home: public demo stats failed to load', e));
        }
      })
      .catch((e) => {
        setClients([]);
        fail('the client list')(e);
      });

    loadWeekOfDeploys()
      .then(setWeek)
      .catch((e) => {
        setWeek([]);
        fail('recent deploys')(e);
      });

    loadLiveImages()
      .then(setLive)
      .catch((e) => {
        setLive({ clientsByImage: new Map(), deployByClient: new Map() });
        fail('which image each client runs')(e);
      });

    Promise.all([getEcrService('numa-deploy').getAllImages(), getEcrService('numa-deploy-dev').getAllImages()])
      .then(([prod, dev]) => setImages([...prod, ...dev]))
      .catch((e) => console.error('Home: image names failed to load', e));

    activityService
      .getRecentActivities({ limit: 8 })
      .then(setActivity)
      .catch((e) => {
        setActivity([]);
        fail('recent activity')(e);
      });
  }, []);

  // A client marked inactive is a stack nobody uses. It is left out of every count on this page.
  const flags = useClientFlags();
  const tagsLoading = flags.status === 'loading';
  const customers = useMemo(
    () => (clients || []).filter((c) => !c.config.devInstance && !flags.isInactive(c.name)),
    [clients, flags]
  );
  const inactiveCustomers = useMemo(
    () => (clients || []).filter((c) => !c.config.devInstance && flags.isInactive(c.name)).length,
    [clients, flags]
  );
  const devCount = useMemo(() => (clients || []).filter((c) => c.config.devInstance).length, [clients]);
  // The Client checks that need no usage data: Home does not load the fleet index.
  const checks = useMemo(
    () => (clients && flags.status === 'ready' ? recordSummary(clients, flags.row, flags.isInactive, nzToday()) : null),
    [clients, flags]
  );

  const deploys = useMemo(
    () => (week || []).filter((dep) => dep.entityType !== 'group' && dep.mode !== 'plan'),
    [week]
  );
  const running = useMemo(() => (week || []).filter((dep) => dep.entityType !== 'group' && isLive(dep)), [week]);
  const finished = useMemo(() => deploys.filter((dep) => isSuccess(dep) || isFailed(dep)), [deploys]);
  const successRate = finished.length ? Math.round((finished.filter(isSuccess).length / finished.length) * 100) : null;

  /**
   * A client needs a look when its NEWEST deploy this week failed. A failure
   * followed by a good deploy is history, not a problem.
   */
  const needsLook = useMemo(() => {
    const newest = new Map<string, DeploymentRecord>();
    for (const dep of deploys) {
      if (isLive(dep)) continue;
      if (!newest.has(dep.clientName)) newest.set(dep.clientName, dep);
    }
    return [...newest.values()].filter((dep) => isFailed(dep) && !flags.isInactive(dep.clientName));
  }, [deploys, flags]);

  const imageName = useMemo(() => {
    const names = new Map<string, string>();
    for (const img of images) names.set(liveImageKey(img.repository, img.tag), img.customName || img.tag);
    return names;
  }, [images]);

  /** The images active customers run, most common first. Dev stacks are left out: they are not a release. */
  const rollout = useMemo(() => {
    if (!live || !clients) return null;
    const customerNames = new Set(customers.map((c) => c.name));
    const rows = [...live.clientsByImage.entries()]
      .map(([key, names]) => ({
        key,
        tag: key.split('#')[1],
        dev: key.startsWith('numa-deploy-dev#'),
        clients: names.filter((name) => customerNames.has(name)),
      }))
      .filter((row) => row.clients.length > 0)
      .sort((a, b) => b.clients.length - a.clients.length);
    const counted = rows.reduce((sum, row) => sum + row.clients.length, 0);
    return { rows, counted, missing: customerNames.size - counted };
  }, [live, clients, customers]);

  const name = firstName(user?.username, user?.email);
  const commonTools = COMMON_TOOL_IDS.map((id) => AVAILABLE_TOOLS.find((tool) => tool.id === id)).filter(
    (tool): tool is (typeof AVAILABLE_TOOLS)[number] => Boolean(tool)
  );
  const demoToday = demo.reduce((sum, d) => sum + d.todayCostUsd, 0);
  const demoLimit = demo.reduce((sum, d) => sum + d.dailyLimitUsd, 0);
  const waiting = <Spinner animation="border" size="sm" className="home-spinner" />;

  return (
    <div className="home-page">
      <PageHeader
        title={name ? `${greeting()}, ${name}` : greeting()}
        actions={
          <Link to="/deployments" className="btn btn-primary">
            <Rocket className="me-2" />
            Start a deployment
          </Link>
        }
      />

      {error && <div className="alert alert-warning">{error}</div>}

      <Section
        title="Right now"
        note={
          checks && (
            <Link
              to="/client-checks"
              className="home-more"
              title="Clients with no contract, or a contract that disagrees with who pays AWS, and trials that ended. The page also checks use."
            >
              Client checks: {plural(checks.recordProblems, 'record to fix', 'records to fix')},{' '}
              {plural(checks.trialsEnded, 'trial ended', 'trials ended')} <ArrowRight />
            </Link>
          )
        }
      >
        <KpiGrid>
          <Kpi
            label="Deploys running"
            scope="now"
            value={week === null ? waiting : running.length}
            sub={
              running.length
                ? running
                    .slice(0, 3)
                    .map((dep) => dep.clientName)
                    .join(', ') + (running.length > 3 ? ` and ${running.length - 3} more` : '')
                : 'None in progress'
            }
          />
          <Kpi
            label="Clients that need a look"
            value={week === null ? waiting : needsLook.length}
            tone={needsLook.length ? 'bad' : 'default'}
            sub="Newest deploy in the last 7 days failed. Inactive clients are left out"
          />
          <Kpi
            label="Deploys, last 7 days"
            value={week === null ? waiting : deploys.length.toLocaleString()}
            sub={successRate === null ? 'None finished yet' : `${successRate}% of finished deploys succeeded`}
          />
          <Kpi
            label="Customer stacks"
            scope="now"
            value={clients === null || tagsLoading ? waiting : customers.length.toLocaleString()}
            sub={
              clients === null || tagsLoading
                ? ''
                : flags.status === 'error'
                  ? `The inactive tags could not be read. ${plural(devCount, 'dev stack', 'dev stacks')} beside them`
                  : `Active only. ${inactiveCustomers.toLocaleString()} inactive, ${plural(devCount, 'dev stack', 'dev stacks')} beside them`
            }
          />
        </KpiGrid>
      </Section>

      <div className="home-columns">
        <div>
          <Panel
            title="Needs a look"
            subtitle="Clients whose newest deploy this week failed."
            flush
            actions={
              <Link to="/deployments" className="home-more">
                All deployments <ArrowRight />
              </Link>
            }
          >
            {week === null && <Empty>{waiting}</Empty>}
            {week !== null && needsLook.length === 0 && (
              <Empty>
                <CheckCircle className="home-ok" /> Every client's newest deploy this week succeeded.
              </Empty>
            )}
            {needsLook.length > 0 && (
              <ul className="home-list">
                {needsLook.map((dep) => (
                  <li key={dep.deploymentId} className="home-row">
                    <ExclamationTriangle className="home-row__mark home-row__mark--bad" aria-hidden="true" />
                    <div className="home-row__main">
                      <div className="home-row__title">{dep.clientName}</div>
                      <div className="home-row__detail">
                        {imageName.get(liveImageKey(dep.repository, dep.imageTag || '')) || dep.imageTag}
                        {dep.deploymentLabel ? ` · ${dep.deploymentLabel}` : ''}
                      </div>
                    </div>
                    <div className="home-row__when" title={formatNzDateTime(dep.startedAt)}>
                      {timeAgo(dep.startedAt)}
                    </div>
                    <Link
                      to={`/deployments?client=${encodeURIComponent(dep.clientName)}`}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      See why
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel
            title="What customers run"
            scope="now"
            subtitle={`Each active customer counted by its newest successful deploy in the last ${LIVE_LOOKBACK_DAYS} days.`}
            actions={
              <Link to="/containers" className="home-more">
                All images <ArrowRight />
              </Link>
            }
          >
            {!rollout && <Empty>{waiting}</Empty>}
            {rollout && rollout.rows.length === 0 && <Empty>No customer deploys in that time.</Empty>}
            {rollout && rollout.rows.length > 0 && (
              <>
                <ul className="home-rollout">
                  {rollout.rows.slice(0, 6).map((row) => (
                    <li key={row.key} className="home-rollout__row" title={row.clients.join('\n')}>
                      <div className="home-rollout__name">
                        <span>{imageName.get(row.key) || row.tag}</span>
                        {row.dev && <span className="badge bg-warning">Dev</span>}
                      </div>
                      <div className="home-rollout__bar">
                        <span style={{ width: `${Math.max(2, (row.clients.length / customers.length) * 100)}%` }} />
                      </div>
                      <div className="home-rollout__count numeric">
                        {plural(row.clients.length, 'customer', 'customers')}
                      </div>
                    </li>
                  ))}
                </ul>
                {(rollout.rows.length > 6 || rollout.missing > 0) && (
                  <p className="home-note">
                    {rollout.rows.length > 6 &&
                      `${plural(rollout.rows.length - 6, 'more image', 'more images')} with fewer customers. `}
                    {rollout.missing > 0 &&
                      `${plural(rollout.missing, 'customer has', 'customers have')} no successful deploy in that time.`}
                  </p>
                )}
              </>
            )}
          </Panel>
        </div>

        <div>
          <Panel
            title="Recent activity"
            flush
            actions={
              <Link to="/activity" className="home-more">
                All activity <ArrowRight />
              </Link>
            }
          >
            {activity === null && <Empty>{waiting}</Empty>}
            {activity !== null && activity.length === 0 && <Empty>Nothing recorded yet.</Empty>}
            {activity && activity.length > 0 && (
              <ul className="home-list">
                {activity.map((record) => {
                  const text = describeActivity(record);
                  return (
                    <li key={record.activityId} className="home-row">
                      <div className="home-row__main">
                        <div className="home-row__title">
                          {text.link ? <Link to={text.link}>{text.title}</Link> : text.title}
                          {text.failed && <span className="badge bg-danger ms-2">Failed</span>}
                        </div>
                        <div className="home-row__detail">{[text.who, text.detail].filter(Boolean).join(' · ')}</div>
                      </div>
                      <div className="home-row__when" title={formatNzDateTime(record.timestamp)}>
                        {timeAgo(record.timestamp)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          {demo.length > 0 && (
            <Panel
              title="Public demo"
              subtitle="What the open demo has spent today against its daily limit."
              actions={
                <Link to={`/public-demo-conversations/${demo[0].clientName}`} className="home-more">
                  Conversations <ArrowRight />
                </Link>
              }
            >
              <div className="home-demo">
                <div className="home-demo__value numeric">
                  US${demoToday.toFixed(2)} <span>of US${demoLimit.toFixed(2)}</span>
                </div>
                <div className="progress">
                  <div
                    className={`progress-bar${demoLimit && demoToday / demoLimit >= 0.85 ? ' bg-danger' : ''}`}
                    style={{ width: `${demoLimit ? Math.min(100, (demoToday / demoLimit) * 100) : 0}%` }}
                  />
                </div>
                <div className="home-note">
                  {plural(
                    demo.reduce((sum, d) => sum + d.thirtyDayUniqueConversations, 0),
                    'conversation',
                    'conversations'
                  )}{' '}
                  and US${demo.reduce((sum, d) => sum + d.thirtyDayCostUsd, 0).toFixed(2)} in the last 30 days.
                </div>
              </div>
            </Panel>
          )}
        </div>
      </div>

      <Section
        title="Common tasks"
        note={
          <Link to="/tools" className="home-more">
            All {AVAILABLE_TOOLS.length} tools <ArrowRight />
          </Link>
        }
      >
        <div className="tool-grid">
          {commonTools.map((tool) => (
            <ToolCard key={tool.id} tool={tool} />
          ))}
        </div>
      </Section>
    </div>
  );
}
